import { createContext, useEffect, useRef } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { fetcher } from 'services/helpers/responseHandler'
import { completeMagicLinkLogin } from 'services/helpers/completeMagicLinkLogin'
import { stopMagicLinkLoginPolling } from 'services/helpers/magicLinkLoginPolling'

import { gotSocketMessage, wsConnected, wsDisconnected } from '../../../actions/socket'
import config from '../../../services/config'
import { clearRequestInterval, requestInterval } from '../../../utils/requestInterval'

const types = {
  phoneLogin: 200,
  codeLogin: 201,
  confirmLogin: 'DashboardConfirmLogin',
  logout: 220
}

const nonJSONresponses = [
  'not understood',
  'invalid json',
  'invalid request',
  'pong',
  'Invalid session'
]

export const SocketContext = createContext()

const log = async ({ level = 'Info', message = '', additionalData = {} }) => {
  try {
    fetcher.post(
      'logs',
      {
        level,
        message,
        additionalData: {
          ...additionalData
        }
      },
      null,
      false
    )
  } catch (error) {
    console.warn('failed to post log', error)
  }
}

let ws
let activeLoginSessionId = null
let reconnectTimeoutId = null

function clearReconnectTimeout() {
  if (reconnectTimeoutId != null) {
    clearTimeout(reconnectTimeoutId)
    reconnectTimeoutId = null
  }
}

function scheduleLoginSocketReconnect(dispatch, sessionId) {
  if (!sessionId || reconnectTimeoutId != null) {
    return
  }

  reconnectTimeoutId = setTimeout(() => {
    reconnectTimeoutId = null
    connectLoginSocket(dispatch, sessionId)
  }, 2000)
}

export function connectLoginSocket(dispatch, sessionId) {
  if (!sessionId) {
    return
  }

  activeLoginSessionId = sessionId

  if (ws && ws.readyState === WebSocket.OPEN && activeLoginSessionId === sessionId) {
    return
  }

  if (ws && ws.readyState !== WebSocket.CLOSED) {
    try {
      ws.close()
    } catch (error) {
      console.warn('failed to close login socket before reconnect', error)
    }
  }

  clearReconnectTimeout()
  ws = new WebSocket(`${config.socketUrl}?sessionId=${sessionId}`)
  ws.onopen = function () {
    log({
      level: 'Debug',
      message: 'Connected Login Socket',
      additionalData: { socketUrl: `${config.socketUrl}?sessionId=${sessionId}` }
    })
    dispatch(wsConnected())
  }

  ws.onclose = function () {
    log({
      level: 'Debug',
      message: 'Closing login socket',
      additionalData: { socketUrl: `${config.socketUrl}?sessionId=${sessionId}` }
    })
    dispatch(wsDisconnected())

    if (activeLoginSessionId === sessionId) {
      scheduleLoginSocketReconnect(dispatch, sessionId)
    }
  }

  ws.onerror = function (err) {
    log({
      level: 'Warn',
      message: `Socket encountered error: ${err.message} Closing login socket`,
      additionalData: { socketUrl: `${config.socketUrl}?sessionId=${sessionId}` }
    })
    console.error('Socket encountered error: ', err.message, 'Closing socket')
    ws.close()
  }
}

export function disconnectLoginSocket() {
  activeLoginSessionId = null
  clearReconnectTimeout()

  log({
    level: 'Debug',
    message: 'Disconnected Login Socket'
  })

  if (ws) {
    ws.close()
  }
}

const SocketProvider = ({ children }) => {
  const dispatch = useDispatch()
  const { connected } = useSelector(s => s.socket)
  const { userLoggedIn } = useSelector(s => s.login)
  const pendingSessionId = useSelector(s => s.signIn.pendingSessionId)
  const loginStep = useSelector(s => s.signIn.loginStep)

  let pingInterval = useRef()

  function setPingInterval() {
    if (pingInterval.current) {
      clearRequestInterval(pingInterval.current)
    }

    pingInterval.current = requestInterval(() => {
      if (ws.readyState === WebSocket.OPEN) {
        ws.send('ping')
      } else {
        clearRequestInterval(pingInterval.current)
      }
    }, 4000)
  }

  function connectSocket(sessionId) {
    if (!connected && (!ws || (ws && ws.readyState === 3))) {
      connectLoginSocket(dispatch, sessionId, log)
    }
  }

  useEffect(() => {
    if (connected && ws) {
      setPingInterval()
    }
  }, [connected])

  useEffect(() => {
    if (userLoggedIn) {
      activeLoginSessionId = null
      clearReconnectTimeout()
      stopMagicLinkLoginPolling()

      if (ws && ws.readyState === WebSocket.OPEN) {
        ws.close()
        clearRequestInterval(pingInterval.current)
      }
    }
  }, [userLoggedIn])

  useEffect(() => {
    if (
      pendingSessionId &&
      (loginStep === 'sentRequest' || loginStep === 'sentRequestAndOpenedSocket') &&
      (!ws || ws.readyState === WebSocket.CLOSED)
    ) {
      connectLoginSocket(dispatch, pendingSessionId)
    }
  }, [pendingSessionId, loginStep, dispatch])

  useEffect(() => {
    if (connected) {
      ws.onmessage = e => {
        if (nonJSONresponses.includes(e.data)) return

        const res = JSON.parse(e.data)

        if (res.type === types.confirmLogin) {
          const { data } = res

          if (completeMagicLinkLogin(dispatch, data)) {
            activeLoginSessionId = null
            clearReconnectTimeout()
            stopMagicLinkLoginPolling()
          }
        } else {
          dispatch(gotSocketMessage(res))
        }
      }
    }
  }, [connected, dispatch])

  return (
    <SocketContext.Provider value={[ws, setPingInterval, connectSocket]}>
      {children}
    </SocketContext.Provider>
  )
}

export default SocketProvider
